import { DIRECTIONS } from "../consts/directions.js";
import { GAME_ACTIONS } from "../consts/game.js";
import { decode62, encode62 } from "../utils/encode.js";
import remember from "../utils/remember.js";

export default class GamePiece {

    constructor({ piece, location, followingTooLongPieceId, gameBoard }) {
        const { id, teamIndex, value } = piece;
        this.id = id;
        this.teamIndex = teamIndex;
        this.value = value;
        this.followingTooLongPieceId = followingTooLongPieceId;
        this.location = location;
        this.gameBoard = gameBoard;
    }

    get isActive() { return true; }

    getNextNonEmptyPieceByDirection = direction => remember(this, `__nextNonBlankByDirection<${direction}>`, () => {
        const nextPiece = this.gameBoard.getAdjacentPieceByLocationAndDirection(this.location.y, this.location.x, direction);

        if (nextPiece && !nextPiece.isEmpty) return nextPiece;

        // keeps looking for a piece that is not blank
        return nextPiece?.getNextNonEmptyPieceByDirection(direction);
    });

    getPieceToMoveToByDirection = direction => remember(this, `__pieceToMoveToByDirection<${direction}>`, () => {
        return this.gameBoard.getAdjacentPieceByLocationAndDirection(this.location.y, this.location.x, direction);
    });

    isInvalidMoveByDirectionDueToFollowing = direction => remember(this, `__isInvalidMoveByDirectionDueToFollowing<${direction}>`, () => {
        if (this.followingTooLongPieceId === undefined) return;

        const pieceToMoveTo = this.gameBoard.getAdjacentPieceByLocationAndDirection(this.location.y, this.location.x, direction) || {};
        return this.gameBoard
            .getAllAdjacentPiecesByLocation(pieceToMoveTo?.location?.y, pieceToMoveTo?.location?.x)
            ?.some(({ id }) => id === this.followingTooLongPieceId);
    });

    get allInvalidMovePieces() {
        return [DIRECTIONS.UP, DIRECTIONS.DOWN, DIRECTIONS.LEFT, DIRECTIONS.RIGHT]
            .map(direction => this.isInvalidMoveByDirectionDueToFollowing(direction) && this.gameBoard.getAdjacentPieceByLocationAndDirection(this.location.y, this.location.x, direction))
            .filter(Boolean);
    }

    canMoveByDirection = direction => remember(this, `__canMoveByDirection<${direction}>`, () => {
        const pieceToMoveTo = this.gameBoard.getAdjacentPieceByLocationAndDirection(this.location.y, this.location.x, direction);
        const isInvalidByDirection = this.isInvalidMoveByDirectionDueToFollowing(direction);
        return !isInvalidByDirection && pieceToMoveTo?.isEmpty;
    });

    get allAvailablePiecesToMoveTo() {
        return remember(this, `__allAvailablePiecesToMoveTo`, () => (
            [DIRECTIONS.UP, DIRECTIONS.DOWN, DIRECTIONS.LEFT, DIRECTIONS.RIGHT].map(direction => (
                this.canMoveByDirection(direction) && this.gameBoard.getAdjacentPieceByLocationAndDirection(this.location.y, this.location.x, direction)
            )).filter(Boolean)
        ));
    }

    getPieceToAttackByDirection = direction => remember(this, `__pieceToAttackByDirection<${direction}>`, () => {
        return this.gameBoard.getAdjacentPieceByLocationAndDirection(this.location.y, this.location.x, direction);
    });

    canAttackByDirection = direction => remember(this, `__canAttackByDirection<${direction}>`, () => {
        const {
            value,
            teamIndex,
        } = this;
        const pieceToAttack = this.getPieceToAttackByDirection(direction);

        if (!pieceToAttack) return false;
        if (pieceToAttack.isEmpty) return false;
        if (pieceToAttack.isUnturned) return false;
        if (teamIndex === pieceToAttack.teamIndex) return false;

        // can attack anything next to it that is of equal or lesser value
        return pieceToAttack.value <= value;
    });

    get allAvailablePiecesToAttack() {
        return remember(this, `__allAvailablePiecesToAttack`, () => (
            [DIRECTIONS.UP, DIRECTIONS.DOWN, DIRECTIONS.LEFT, DIRECTIONS.RIGHT].map(direction => (
                this.canAttackByDirection(direction) && this.getPieceToAttackByDirection(direction)
            )).filter(Boolean)
        ));
    }

    get allAvailablePiecesForAction() {
        return remember(this, `__allAvailablePiecesForAction`, () => [
            ...this.allAvailablePiecesToAttack,
            ...this.allAvailablePiecesToMoveTo,
        ]);
    }

    isValidAction = (actionName, direction) => {
        if (actionName === GAME_ACTIONS.FLIP) return this.canFlip;
        if (actionName === GAME_ACTIONS.MOVE) return this.canMoveByDirection(direction);
        if (actionName === GAME_ACTIONS.ATTACK) return this.canAttackByDirection(direction);
    }

    get encodedPiece() {
        const {
            id,
            teamIndex = -1,
            value,
        } = this;

        return [encode62(id), teamIndex, value].join(',');
    }

    static decodePiece = encodedPiece => {
        const [encodedId, teamIndex, value] = encodedPiece.split(',');
        return {
            id: decode62(encodedId),
            teamIndex: +teamIndex,
            value: +value,
        }
    }
}